import { BASE, LIGHT } from '../../palette';
import { pt, rect, type Pt } from './geom';
import { halftoneDots, hazardBands } from './hatch';
import { dot, fillPoly, line, solid, type Prim } from './spec';

/**
 * **被两个新资产共用的构件。**
 *
 * 牌背和标题 Logo 不是两张各画各的图 —— 它们是**同一台机器的两种印张**，
 * 所以共用同一套织纹、同一个标记、同一套角标。
 * 这也是它们必须走同一条 spec 管线的原因：共用构件比共用"风格"可靠得多。
 */

// ============================================================
// 织纹：一道线屏 + 一道点屏
// ============================================================

export interface WeaveOpts {
  /** 线屏 */
  readonly linePitch: number;
  readonly lineAngle: number;
  readonly lineDuty: number;
  readonly lineAlpha: number;
  /** 点屏。⚠️ 和线屏**角度不同**才会出摩尔纹 —— 同角度就只是叠加 */
  readonly dotPitch: number;
  readonly dotAngle: number;
  readonly dotCoverage: number;
  readonly dotAlpha: number;
  readonly color?: number;
}

/**
 * 套印织纹。
 *
 * 线屏和点屏交叉，出来的摩尔纹从远处看是一块布、凑近看是网点乱撞 ——
 * 这正是廉价印刷品防伪纸的样子，也正是这一整套视觉的来源。
 *
 * ⚠️ 用的还是 `hatch.ts` 那两个函数，所以它和**危险条纹是同一套几何**：
 * 牌背的织纹、异常牌底部的危险条、濒死横带，都出自同一处。
 */
export function weaveField(poly: readonly Pt[], o: WeaveOpts): Prim[] {
  const color = o.color ?? LIGHT.base;
  const out: Prim[] = [];
  for (const band of hazardBands(poly, o.linePitch, o.lineAngle, o.lineDuty)) {
    out.push(fillPoly(band, solid(color, o.lineAlpha)));
  }
  for (const d of halftoneDots(poly, o.dotPitch, o.dotAngle, o.dotCoverage)) {
    out.push(dot(d.x, d.y, d.r, solid(color, o.dotAlpha)));
  }
  return out;
}

// ============================================================
// 标记：两块没对准的版
// ============================================================

export interface SigilOpts {
  readonly x: number;
  readonly y: number;
  readonly w: number;
  readonly h: number;
  /** 鬼影版的偏移量。方向就是套印错位的方向 */
  readonly ghostX: number;
  readonly ghostY: number;
  /** 版的描边宽度。0 = 不描边 */
  readonly stroke: number;
  readonly ghostAlpha?: number;
}

/**
 * **两块没对准的版。**
 *
 * 这是全套视觉的**图腾**：同一个形状印了两遍，第二遍没对齐。
 * 「一台坏掉的印刷机」这句话，一个图形就说完了。
 *
 * ⚠️ 形状是**卡的形状**（2.5 : 3.5）—— 所以它读起来是"同一张牌印了两次"。
 * 牌背中央和标题 Logo 用的是同一个函数，这是两个资产的纽带。
 */
export function plateSigil(o: SigilOpts): Prim[] {
  const ghost = rect(o.x - o.w / 2 + o.ghostX, o.y - o.h / 2 + o.ghostY, o.w, o.h);
  const main = rect(o.x - o.w / 2, o.y - o.h / 2, o.w, o.h);
  const out: Prim[] = [fillPoly(ghost, solid(LIGHT.base, o.ghostAlpha ?? 0.92))];
  out.push(
    o.stroke > 0
      ? fillPoly(main, solid(BASE.void), { width: o.stroke, color: LIGHT.base })
      : fillPoly(main, solid(BASE.void)),
  );
  return out;
}

// ============================================================
// 角标：裁切/套准标记
// ============================================================

/**
 * 四个角的 L 形角标。
 *
 * 印张被**裁歪**的痕迹 —— 它不是装饰，是"这张纸是从一大张上切下来的"的证据。
 * 所以角标故意贴边、故意不对称（四个角的臂长各不相同，因为有刀口误差）。
 */
export function cornerMarks(
  w: number,
  h: number,
  inset: number,
  arm: number,
  color: number,
  alpha: number,
  stroke: number,
): Prim[] {
  const out: Prim[] = [];
  const corners: readonly (readonly [number, number, number, number, number])[] = [
    [0, 0, 1, 1, 1],
    [w, 0, -1, 1, 0.86],
    [0, h, 1, -1, 0.92],
    [w, h, -1, -1, 1.08],
  ];
  for (const [cx, cy, sx, sy, k] of corners) {
    const x = cx + sx * inset;
    const y = cy + sy * inset;
    const len = arm * k;
    out.push(
      line([pt(x, y), pt(x + sx * len, y)], { width: stroke, color, alpha }),
      line([pt(x, y), pt(x, y + sy * len)], { width: stroke, color, alpha }),
    );
  }
  return out;
}
